/**
 * Table Component
 *
 * Conjunto de componentes para construção de tabelas com suporte a dark mode,
 * hover, seleção e ordenação. Composto por: Table, TableHead, TableBody,
 * TableRow, TableHeaderCell e TableCell.
 *
 * @module components/ui
 * @since 2025-01-03
 *
 * @example
 * // Tabela básica com dados
 * <Table>
 *   <TableHead>
 *     <TableRow>
 *       <TableHeaderCell>Nome</TableHeaderCell>
 *       <TableHeaderCell>Email</TableHeaderCell>
 *       <TableHeaderCell>Status</TableHeaderCell>
 *     </TableRow>
 *   </TableHead>
 *   <TableBody>
 *     {users.map((user) => (
 *       <TableRow key={user.id}>
 *         <TableCell>{user.name}</TableCell>
 *         <TableCell>{user.email}</TableCell>
 *         <TableCell><Badge>{user.status}</Badge></TableCell>
 *       </TableRow>
 *     ))}
 *   </TableBody>
 * </Table>
 *
 * @example
 * // Tabela com ordenação
 * <TableHeaderCell
 *   sortable
 *   sorted={sortField === 'name' ? sortDirection : null}
 *   onClick={() => handleSort('name')}
 * >
 *   Nome
 * </TableHeaderCell>
 *
 * @example
 * // Linha selecionada
 * <TableRow selected={selectedId === row.id} onClick={() => setSelectedId(row.id)}>
 *   ...
 * </TableRow>
 */

import { forwardRef, type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

// Tipo de alinhamento reutilizado por headers e cells
export type TableAlign = 'left' | 'center' | 'right';

const headerAlignClasses: Record<TableAlign, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

const cellAlignClasses: Record<TableAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

// ============================================
// TABLE
// ============================================

/**
 * Props do componente Table
 */
interface TableProps extends HTMLAttributes<HTMLTableElement> {
  /** Se true, alterna cores das linhas @default false */
  striped?: boolean;
  /** Se true, destaca linha ao passar o mouse @default true */
  hoverable?: boolean;
  /** Se true, reduz padding das células @default false */
  compact?: boolean;
  /** Se true, envolve a tabela em um container com overflow-x-auto @default true */
  wrap?: boolean;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, striped = false, hoverable = true, compact = false, wrap = true, children, ...props }, ref) => {
    const tableEl = (
      <table
        ref={ref}
        className={cn('w-full text-sm text-left text-foreground', className)}
        {...props}
      >
        {children}
      </table>
    );

    if (!wrap) return tableEl;

    return <div className="overflow-x-auto">{tableEl}</div>;
  }
);

Table.displayName = 'Table';

// ============================================
// TABLE HEAD
// ============================================

type TableHeadProps = HTMLAttributes<HTMLTableSectionElement>;

export const TableHead = forwardRef<HTMLTableSectionElement, TableHeadProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <thead
        ref={ref}
        className={cn('bg-muted/50 border-b border-border', className)}
        {...props}
      >
        {children}
      </thead>
    );
  }
);

TableHead.displayName = 'TableHead';

// ============================================
// TABLE BODY
// ============================================

interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  striped?: boolean;
  hoverable?: boolean;
}

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, striped = false, hoverable = true, children, ...props }, ref) => {
    return (
      <tbody
        ref={ref}
        className={cn('divide-y divide-border/50', className)}
        {...props}
      >
        {children}
      </tbody>
    );
  }
);

TableBody.displayName = 'TableBody';

// ============================================
// TABLE ROW
// ============================================

interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  hoverable?: boolean;
  selected?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, hoverable = true, selected = false, children, ...props }, ref) => {
    return (
      <tr
        ref={ref}
        className={cn(
          'transition-colors',
          hoverable && 'hover:bg-muted/50',
          selected && 'bg-primary/10',
          className
        )}
        {...props}
      >
        {children}
      </tr>
    );
  }
);

TableRow.displayName = 'TableRow';

// ============================================
// TABLE HEADER CELL
// ============================================

interface TableHeaderCellProps extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'align'> {
  sortable?: boolean;
  sorted?: 'asc' | 'desc' | null;
  /** Alinhamento horizontal do conteúdo @default 'left' */
  align?: TableAlign;
}

export const TableHeaderCell = forwardRef<HTMLTableCellElement, TableHeaderCellProps>(
  ({ className, sortable = false, sorted = null, align = 'left', children, ...props }, ref) => {
    return (
      <th
        ref={ref}
        className={cn(
          'px-4 py-3 text-xs font-medium uppercase tracking-wider',
          'text-muted-foreground',
          sortable && 'cursor-pointer select-none hover:text-foreground',
          className
        )}
        {...props}
      >
        <div className={cn('flex items-center gap-1', headerAlignClasses[align])}>
          {children}
          {sorted && (
            <span className="text-primary">
              {sorted === 'asc' ? '↑' : '↓'}
            </span>
          )}
        </div>
      </th>
    );
  }
);

TableHeaderCell.displayName = 'TableHeaderCell';

// ============================================
// TABLE CELL
// ============================================

export interface TableCellProps extends Omit<TdHTMLAttributes<HTMLTableCellElement>, 'align'> {
  /** Alinhamento horizontal do conteúdo @default 'left' */
  align?: TableAlign;
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, align, children, ...props }, ref) => {
    return (
      <td
        ref={ref}
        className={cn(
          'px-4 py-3 text-foreground',
          align && cellAlignClasses[align],
          className
        )}
        {...props}
      >
        {children}
      </td>
    );
  }
);

TableCell.displayName = 'TableCell';

// ============================================
// EXPORTS
// ============================================

export default Table;
